import { Helmet } from 'react-helmet-async'

// CANONICAL HOST. Chosen 2026-08-17: www, not the bare domain.
// Both hostnames served the whole site independently with no redirect, so
// Google could see two complete copies. www wins because it resolves straight
// to Firebase Hosting, which takes Cloudflare out of the serving path.
// If this ever changes, it must change in config/schema.ts and
// scripts/gen-sitemap.mjs in the same commit or the signals split again.
const SITE_URL = 'https://www.advantagehorsemanship.com'
const DEFAULT_OG_IMAGE = `${SITE_URL}/icons/icon-512.png`

interface SEOProps {
  title: string
  description: string
  path: string
  ogType?: 'website' | 'article' | 'product' | 'profile' | 'event' | 'service'
  image?: string
  /** Optional JSON-LD structured data (single object or array) */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[]
  /**
   * Keep this page out of search results. For utility pages that are public
   * but worthless as landing pages (thank-you, confirmations). Note this does
   * NOT hide a page from AI crawlers that ignore robots directives; it is an
   * instruction, not a lock.
   */
  noindex?: boolean
}

export function SEO({
  title,
  description,
  path,
  ogType = 'website',
  image,
  jsonLd,
  noindex = false,
}: SEOProps) {
  const canonical = `${SITE_URL}${path}`
  const ogImage = image ?? DEFAULT_OG_IMAGE
  // OG spec only has a fixed set of base types. Map custom values to closest match.
  const ogTypeAttr = ogType === 'product' || ogType === 'event' || ogType === 'service' ? 'website' : ogType

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={canonical} />
      {noindex && <meta name="robots" content="noindex, follow" />}

      {/* Open Graph */}
      <meta property="og:type" content={ogTypeAttr} />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:site_name" content="Advantage Horsemanship" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {/* JSON-LD structured data */}
      {jsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(
            Array.isArray(jsonLd)
              ? { '@context': 'https://schema.org', '@graph': jsonLd }
              : { '@context': 'https://schema.org', ...jsonLd },
          )}
        </script>
      )}
    </Helmet>
  )
}
